import type { RangeKey } from '@dataTypes/dashboard.types'

export type RecallKpiData = {
  completedRecall: number
  completedRecallGoal: number
  overduePatients: string
  scheduledRecall: string
  recallRate: string
  recallReactivation: string
  recallProduction: string
  comparisonLabel: string
  completedDelta: string
  completedTrend: 'up' | 'down'
  overdueDelta: string
  overdueTrend: 'up' | 'down'
  productionDelta: string
  productionTrend: 'up' | 'down'
}

const RECALL_KPI: Record<RangeKey, RecallKpiData> = {
  '1D': {
    completedRecall: 22,
    completedRecallGoal: 28,
    overduePatients: '$14.6K',
    scheduledRecall: '95%',
    recallRate: '95%',
    recallReactivation: '95%',
    recallProduction: '$14.6K',
    comparisonLabel: 'vs. yesterday',
    completedDelta: '10% (2 patients)',
    completedTrend: 'up',
    overdueDelta: '5% (3 patients)',
    overdueTrend: 'up',
    productionDelta: '5% ($730)',
    productionTrend: 'up',
  },
  '1W': {
    completedRecall: 110,
    completedRecallGoal: 140,
    overduePatients: '$73.0K',
    scheduledRecall: '94%',
    recallRate: '94%',
    recallReactivation: '94%',
    recallProduction: '$51.0K',
    comparisonLabel: 'vs. last week',
    completedDelta: '8% (8 patients)',
    completedTrend: 'up',
    overdueDelta: '4% (5 patients)',
    overdueTrend: 'up',
    productionDelta: '5% ($2.4K)',
    productionTrend: 'up',
  },
  '1M': {
    completedRecall: 440,
    completedRecallGoal: 560,
    overduePatients: '$306K',
    scheduledRecall: '93%',
    recallRate: '93%',
    recallReactivation: '93%',
    recallProduction: '$204K',
    comparisonLabel: 'vs. last month',
    completedDelta: '7% (29 patients)',
    completedTrend: 'up',
    overdueDelta: '5% (6 patients)',
    overdueTrend: 'up',
    productionDelta: '6% ($11.5K)',
    productionTrend: 'up',
  },
  '1Y': {
    completedRecall: 5280,
    completedRecallGoal: 6720,
    overduePatients: '$3.67M',
    scheduledRecall: '92%',
    recallRate: '92%',
    recallReactivation: '92%',
    recallProduction: '$2.45M',
    comparisonLabel: 'vs. last year',
    completedDelta: '9% (436 patients)',
    completedTrend: 'up',
    overdueDelta: '6% (7 patients)',
    overdueTrend: 'up',
    productionDelta: '8% ($181K)',
    productionTrend: 'up',
  },
  YTD: {
    completedRecall: 1320,
    completedRecallGoal: 1680,
    overduePatients: '$918K',
    scheduledRecall: '93%',
    recallRate: '93%',
    recallReactivation: '93%',
    recallProduction: '$612K',
    comparisonLabel: 'vs. YTD last year',
    completedDelta: '8% (98 patients)',
    completedTrend: 'up',
    overdueDelta: '5% (6 patients)',
    overdueTrend: 'up',
    productionDelta: '7% ($40K)',
    productionTrend: 'up',
  },
}

export function getRecallKpi(range: RangeKey): RecallKpiData {
  return RECALL_KPI[range]
}

export type RecallChartPoint = {
  label: string
  completed: number
  scheduled: number
  overdue: number
}

export type RecallChartConfig = {
  data: RecallChartPoint[]
  yAxisDomain: [number, number]
  yAxisTicks: number[]
}

const RECALL_CHART: Record<RangeKey, RecallChartConfig> = {
  '1D': {
    data: [
      { label: '8 AM', completed: 3, scheduled: 4, overdue: 14 },
      { label: '9 AM', completed: 4, scheduled: 5, overdue: 14 },
      { label: '10 AM', completed: 4, scheduled: 5, overdue: 14 },
      { label: '11 AM', completed: 3, scheduled: 4, overdue: 14 },
      { label: '12 PM', completed: 1, scheduled: 2, overdue: 14 },
      { label: '1 PM', completed: 3, scheduled: 4, overdue: 14 },
      { label: '2 PM', completed: 3, scheduled: 4, overdue: 14 },
      { label: '3 PM', completed: 1, scheduled: 2, overdue: 14 },
      { label: '4 PM', completed: 0, scheduled: 2, overdue: 14 },
      { label: '5 PM', completed: 0, scheduled: 1, overdue: 14 },
    ],
    yAxisDomain: [0, 25],
    yAxisTicks: [0, 5, 10, 15, 20, 25],
  },
  '1W': {
    data: [
      { label: 'Mon', completed: 24, scheduled: 28, overdue: 106 },
      { label: 'Tue', completed: 26, scheduled: 30, overdue: 104 },
      { label: 'Wed', completed: 22, scheduled: 26, overdue: 102 },
      { label: 'Thu', completed: 24, scheduled: 28, overdue: 100 },
      { label: 'Fri', completed: 14, scheduled: 18, overdue: 100 },
    ],
    yAxisDomain: [0, 130],
    yAxisTicks: [0, 30, 60, 90, 120],
  },
  '1M': {
    data: [
      { label: 'Wk 1', completed: 110, scheduled: 140, overdue: 106 },
      { label: 'Wk 2', completed: 116, scheduled: 146, overdue: 104 },
      { label: 'Wk 3', completed: 108, scheduled: 138, overdue: 102 },
      { label: 'Wk 4', completed: 106, scheduled: 136, overdue: 100 },
    ],
    yAxisDomain: [0, 160],
    yAxisTicks: [0, 40, 80, 120, 160],
  },
  '1Y': {
    data: [
      { label: 'Apr', completed: 420, scheduled: 530, overdue: 114 },
      { label: 'May', completed: 438, scheduled: 552, overdue: 112 },
      { label: 'Jun', completed: 452, scheduled: 570, overdue: 110 },
      { label: 'Jul', completed: 440, scheduled: 556, overdue: 108 },
      { label: 'Aug', completed: 426, scheduled: 538, overdue: 106 },
      { label: 'Sep', completed: 408, scheduled: 516, overdue: 106 },
      { label: 'Oct', completed: 444, scheduled: 560, overdue: 107 },
      { label: 'Nov', completed: 452, scheduled: 570, overdue: 107 },
      { label: 'Dec', completed: 418, scheduled: 528, overdue: 108 },
      { label: 'Jan', completed: 438, scheduled: 554, overdue: 107 },
      { label: 'Feb', completed: 448, scheduled: 566, overdue: 107 },
      { label: 'Mar', completed: 444, scheduled: 560, overdue: 106 },
    ],
    yAxisDomain: [0, 650],
    yAxisTicks: [0, 130, 260, 390, 520, 650],
  },
  YTD: {
    data: [
      { label: 'Jan', completed: 438, scheduled: 554, overdue: 107 },
      { label: 'Feb', completed: 448, scheduled: 566, overdue: 107 },
      { label: 'Mar', completed: 434, scheduled: 560, overdue: 106 },
    ],
    yAxisDomain: [0, 650],
    yAxisTicks: [0, 130, 260, 390, 520, 650],
  },
}

export function getRecallChartConfig(range: RangeKey): RecallChartConfig {
  return RECALL_CHART[range]
}

export type RecallByProviderRow = {
  id: string
  provider: string
  completed: number
  overdue: number
  scheduled: number
  recallRate: string
  recallProduction: string
  reactivationRate: string
}

const BY_PROVIDER: Record<RangeKey, RecallByProviderRow[]> = {
  '1D': [
    {
      id: '1',
      provider: 'Dr. Alfred Weston',
      completed: 5,
      overdue: 22,
      scheduled: 12,
      recallRate: '62%',
      recallProduction: '$2,300',
      reactivationRate: '55%',
    },
    {
      id: '2',
      provider: 'Dr. Emily Johnson',
      completed: 6,
      overdue: 15,
      scheduled: 10,
      recallRate: '71%',
      recallProduction: '$2,760',
      reactivationRate: '67%',
    },
    {
      id: '3',
      provider: 'Dr. James Thompson',
      completed: 5,
      overdue: 25,
      scheduled: 6,
      recallRate: '50%',
      recallProduction: '$2,300',
      reactivationRate: '48%',
    },
    {
      id: '4',
      provider: 'RDH Sarah Lee',
      completed: 3,
      overdue: 16,
      scheduled: 5,
      recallRate: '52%',
      recallProduction: '$1,380',
      reactivationRate: '50%',
    },
    {
      id: '5',
      provider: 'RDH David Martinez',
      completed: 3,
      overdue: 18,
      scheduled: 11,
      recallRate: '56%',
      recallProduction: '$1,380',
      reactivationRate: '61%',
    },
    {
      id: '6',
      provider: 'RDH Emily Davis',
      completed: 0,
      overdue: 10,
      scheduled: 5,
      recallRate: '81%',
      recallProduction: '$0',
      reactivationRate: '50%',
    },
  ],
  '1W': [
    {
      id: '1',
      provider: 'Dr. Alfred Weston',
      completed: 24,
      overdue: 22,
      scheduled: 12,
      recallRate: '62%',
      recallProduction: '$11,040',
      reactivationRate: '55%',
    },
    {
      id: '2',
      provider: 'Dr. Emily Johnson',
      completed: 28,
      overdue: 15,
      scheduled: 10,
      recallRate: '71%',
      recallProduction: '$12,880',
      reactivationRate: '67%',
    },
    {
      id: '3',
      provider: 'Dr. James Thompson',
      completed: 24,
      overdue: 25,
      scheduled: 6,
      recallRate: '50%',
      recallProduction: '$11,040',
      reactivationRate: '48%',
    },
    {
      id: '4',
      provider: 'RDH Sarah Lee',
      completed: 14,
      overdue: 16,
      scheduled: 5,
      recallRate: '52%',
      recallProduction: '$6,440',
      reactivationRate: '50%',
    },
    {
      id: '5',
      provider: 'RDH David Martinez',
      completed: 14,
      overdue: 18,
      scheduled: 11,
      recallRate: '56%',
      recallProduction: '$6,440',
      reactivationRate: '61%',
    },
    {
      id: '6',
      provider: 'RDH Emily Davis',
      completed: 6,
      overdue: 10,
      scheduled: 5,
      recallRate: '81%',
      recallProduction: '$2,760',
      reactivationRate: '50%',
    },
  ],
  '1M': [
    {
      id: '1',
      provider: 'Dr. Alfred Weston',
      completed: 96,
      overdue: 22,
      scheduled: 12,
      recallRate: '62%',
      recallProduction: '$44,160',
      reactivationRate: '55%',
    },
    {
      id: '2',
      provider: 'Dr. Emily Johnson',
      completed: 112,
      overdue: 15,
      scheduled: 10,
      recallRate: '71%',
      recallProduction: '$51,520',
      reactivationRate: '67%',
    },
    {
      id: '3',
      provider: 'Dr. James Thompson',
      completed: 96,
      overdue: 25,
      scheduled: 6,
      recallRate: '50%',
      recallProduction: '$44,160',
      reactivationRate: '48%',
    },
    {
      id: '4',
      provider: 'RDH Sarah Lee',
      completed: 56,
      overdue: 16,
      scheduled: 5,
      recallRate: '52%',
      recallProduction: '$25,760',
      reactivationRate: '50%',
    },
    {
      id: '5',
      provider: 'RDH David Martinez',
      completed: 56,
      overdue: 18,
      scheduled: 11,
      recallRate: '56%',
      recallProduction: '$25,760',
      reactivationRate: '61%',
    },
    {
      id: '6',
      provider: 'RDH Emily Davis',
      completed: 24,
      overdue: 10,
      scheduled: 5,
      recallRate: '81%',
      recallProduction: '$11,040',
      reactivationRate: '50%',
    },
  ],
  '1Y': [
    {
      id: '1',
      provider: 'Dr. Alfred Weston',
      completed: 1152,
      overdue: 22,
      scheduled: 12,
      recallRate: '62%',
      recallProduction: '$529,920',
      reactivationRate: '55%',
    },
    {
      id: '2',
      provider: 'Dr. Emily Johnson',
      completed: 1344,
      overdue: 15,
      scheduled: 10,
      recallRate: '71%',
      recallProduction: '$618,240',
      reactivationRate: '67%',
    },
    {
      id: '3',
      provider: 'Dr. James Thompson',
      completed: 1152,
      overdue: 25,
      scheduled: 6,
      recallRate: '50%',
      recallProduction: '$529,920',
      reactivationRate: '48%',
    },
    {
      id: '4',
      provider: 'RDH Sarah Lee',
      completed: 672,
      overdue: 16,
      scheduled: 5,
      recallRate: '52%',
      recallProduction: '$309,120',
      reactivationRate: '50%',
    },
    {
      id: '5',
      provider: 'RDH David Martinez',
      completed: 672,
      overdue: 18,
      scheduled: 11,
      recallRate: '56%',
      recallProduction: '$309,120',
      reactivationRate: '61%',
    },
    {
      id: '6',
      provider: 'RDH Emily Davis',
      completed: 288,
      overdue: 10,
      scheduled: 5,
      recallRate: '81%',
      recallProduction: '$132,480',
      reactivationRate: '50%',
    },
  ],
  YTD: [
    {
      id: '1',
      provider: 'Dr. Alfred Weston',
      completed: 288,
      overdue: 22,
      scheduled: 12,
      recallRate: '62%',
      recallProduction: '$132,480',
      reactivationRate: '55%',
    },
    {
      id: '2',
      provider: 'Dr. Emily Johnson',
      completed: 336,
      overdue: 15,
      scheduled: 10,
      recallRate: '71%',
      recallProduction: '$154,560',
      reactivationRate: '67%',
    },
    {
      id: '3',
      provider: 'Dr. James Thompson',
      completed: 288,
      overdue: 25,
      scheduled: 6,
      recallRate: '50%',
      recallProduction: '$132,480',
      reactivationRate: '48%',
    },
    {
      id: '4',
      provider: 'RDH Sarah Lee',
      completed: 168,
      overdue: 16,
      scheduled: 5,
      recallRate: '52%',
      recallProduction: '$77,280',
      reactivationRate: '50%',
    },
    {
      id: '5',
      provider: 'RDH David Martinez',
      completed: 168,
      overdue: 18,
      scheduled: 11,
      recallRate: '56%',
      recallProduction: '$77,280',
      reactivationRate: '61%',
    },
    {
      id: '6',
      provider: 'RDH Emily Davis',
      completed: 72,
      overdue: 10,
      scheduled: 5,
      recallRate: '81%',
      recallProduction: '$33,120',
      reactivationRate: '50%',
    },
  ],
}

export function getRecallByProviderRows(
  range: RangeKey,
): RecallByProviderRow[] {
  return BY_PROVIDER[range]
}

export type RecallOverduePatientRow = {
  id: string
  patientName: string
  lastRecall: string
  daysOverdue: number
  recallType: string
  provider: string
  status: string
}

const OVERDUE_PATIENTS: Record<RangeKey, RecallOverduePatientRow[]> = {
  '1D': [
    {
      id: '1',
      patientName: 'John Doe',
      lastRecall: '09/18/2025',
      daysOverdue: 182,
      recallType: 'Prophy',
      provider: 'RDH Sarah Lee',
      status: 'Contacted',
    },
    {
      id: '2',
      patientName: 'Jane Smith',
      lastRecall: '10/15/2025',
      daysOverdue: 155,
      recallType: 'Perio maintenance',
      provider: 'RDH David Martinez',
      status: 'Not contacted',
    },
    {
      id: '3',
      patientName: 'Alice Johnson',
      lastRecall: '11/02/2025',
      daysOverdue: 137,
      recallType: 'Prophy',
      provider: 'RDH Emily Davis',
      status: 'Scheduled',
    },
    {
      id: '4',
      patientName: 'Michael Brown',
      lastRecall: '08/20/2025',
      daysOverdue: 211,
      recallType: 'Perio maintenance',
      provider: 'RDH Sarah Lee',
      status: 'Not contacted',
    },
    {
      id: '5',
      patientName: 'Emily Davis',
      lastRecall: '11/30/2025',
      daysOverdue: 109,
      recallType: 'Prophy',
      provider: 'RDH David Martinez',
      status: 'Contacted',
    },
    {
      id: '6',
      patientName: 'Chris Lee',
      lastRecall: '07/14/2025',
      daysOverdue: 248,
      recallType: 'Prophy',
      provider: 'RDH Emily Davis',
      status: 'Not contacted',
    },
  ],
  '1W': [
    {
      id: '1',
      patientName: 'John Doe',
      lastRecall: '09/18/2025',
      daysOverdue: 182,
      recallType: 'Prophy',
      provider: 'RDH Sarah Lee',
      status: 'Contacted',
    },
    {
      id: '2',
      patientName: 'Jane Smith',
      lastRecall: '10/15/2025',
      daysOverdue: 155,
      recallType: 'Perio maintenance',
      provider: 'RDH David Martinez',
      status: 'Not contacted',
    },
    {
      id: '3',
      patientName: 'Alice Johnson',
      lastRecall: '11/02/2025',
      daysOverdue: 137,
      recallType: 'Prophy',
      provider: 'RDH Emily Davis',
      status: 'Scheduled',
    },
    {
      id: '4',
      patientName: 'Michael Brown',
      lastRecall: '08/20/2025',
      daysOverdue: 211,
      recallType: 'Perio maintenance',
      provider: 'RDH Sarah Lee',
      status: 'Not contacted',
    },
    {
      id: '5',
      patientName: 'Emily Davis',
      lastRecall: '11/30/2025',
      daysOverdue: 109,
      recallType: 'Prophy',
      provider: 'RDH David Martinez',
      status: 'Contacted',
    },
    {
      id: '6',
      patientName: 'Chris Lee',
      lastRecall: '07/14/2025',
      daysOverdue: 248,
      recallType: 'Prophy',
      provider: 'RDH Emily Davis',
      status: 'Not contacted',
    },
    {
      id: '7',
      patientName: 'Patricia Moore',
      lastRecall: '10/01/2025',
      daysOverdue: 169,
      recallType: 'Perio maintenance',
      provider: 'RDH Sarah Lee',
      status: 'Scheduled',
    },
    {
      id: '8',
      patientName: 'Robert Taylor',
      lastRecall: '12/10/2025',
      daysOverdue: 99,
      recallType: 'Prophy',
      provider: 'RDH David Martinez',
      status: 'Not contacted',
    },
  ],
  '1M': [
    {
      id: '1',
      patientName: 'John Doe',
      lastRecall: '09/18/2025',
      daysOverdue: 182,
      recallType: 'Prophy',
      provider: 'RDH Sarah Lee',
      status: 'Contacted',
    },
    {
      id: '2',
      patientName: 'Jane Smith',
      lastRecall: '10/15/2025',
      daysOverdue: 155,
      recallType: 'Perio maintenance',
      provider: 'RDH David Martinez',
      status: 'Not contacted',
    },
    {
      id: '3',
      patientName: 'Alice Johnson',
      lastRecall: '11/02/2025',
      daysOverdue: 137,
      recallType: 'Prophy',
      provider: 'RDH Emily Davis',
      status: 'Scheduled',
    },
    {
      id: '4',
      patientName: 'Michael Brown',
      lastRecall: '08/20/2025',
      daysOverdue: 211,
      recallType: 'Perio maintenance',
      provider: 'RDH Sarah Lee',
      status: 'Not contacted',
    },
    {
      id: '5',
      patientName: 'Emily Davis',
      lastRecall: '11/30/2025',
      daysOverdue: 109,
      recallType: 'Prophy',
      provider: 'RDH David Martinez',
      status: 'Contacted',
    },
    {
      id: '6',
      patientName: 'Chris Lee',
      lastRecall: '07/14/2025',
      daysOverdue: 248,
      recallType: 'Prophy',
      provider: 'RDH Emily Davis',
      status: 'Not contacted',
    },
    {
      id: '7',
      patientName: 'Patricia Moore',
      lastRecall: '10/01/2025',
      daysOverdue: 169,
      recallType: 'Perio maintenance',
      provider: 'RDH Sarah Lee',
      status: 'Scheduled',
    },
    {
      id: '8',
      patientName: 'Robert Taylor',
      lastRecall: '12/10/2025',
      daysOverdue: 99,
      recallType: 'Prophy',
      provider: 'RDH David Martinez',
      status: 'Not contacted',
    },
    {
      id: '9',
      patientName: 'Linda White',
      lastRecall: '06/22/2025',
      daysOverdue: 270,
      recallType: 'Perio maintenance',
      provider: 'RDH Emily Davis',
      status: 'Not contacted',
    },
    {
      id: '10',
      patientName: 'Kevin Harris',
      lastRecall: '12/01/2025',
      daysOverdue: 108,
      recallType: 'Prophy',
      provider: 'RDH Sarah Lee',
      status: 'Contacted',
    },
  ],
  '1Y': [
    {
      id: '1',
      patientName: 'John Doe',
      lastRecall: '09/18/2025',
      daysOverdue: 182,
      recallType: 'Prophy',
      provider: 'RDH Sarah Lee',
      status: 'Contacted',
    },
    {
      id: '2',
      patientName: 'Jane Smith',
      lastRecall: '10/15/2025',
      daysOverdue: 155,
      recallType: 'Perio maintenance',
      provider: 'RDH David Martinez',
      status: 'Not contacted',
    },
    {
      id: '3',
      patientName: 'Alice Johnson',
      lastRecall: '11/02/2025',
      daysOverdue: 137,
      recallType: 'Prophy',
      provider: 'RDH Emily Davis',
      status: 'Scheduled',
    },
    {
      id: '4',
      patientName: 'Michael Brown',
      lastRecall: '08/20/2025',
      daysOverdue: 211,
      recallType: 'Perio maintenance',
      provider: 'RDH Sarah Lee',
      status: 'Not contacted',
    },
    {
      id: '5',
      patientName: 'Emily Davis',
      lastRecall: '11/30/2025',
      daysOverdue: 109,
      recallType: 'Prophy',
      provider: 'RDH David Martinez',
      status: 'Contacted',
    },
    {
      id: '6',
      patientName: 'Chris Lee',
      lastRecall: '07/14/2025',
      daysOverdue: 248,
      recallType: 'Prophy',
      provider: 'RDH Emily Davis',
      status: 'Not contacted',
    },
    {
      id: '7',
      patientName: 'Patricia Moore',
      lastRecall: '10/01/2025',
      daysOverdue: 169,
      recallType: 'Perio maintenance',
      provider: 'RDH Sarah Lee',
      status: 'Scheduled',
    },
    {
      id: '8',
      patientName: 'Linda White',
      lastRecall: '06/22/2025',
      daysOverdue: 270,
      recallType: 'Perio maintenance',
      provider: 'RDH Emily Davis',
      status: 'Not contacted',
    },
  ],
  YTD: [
    {
      id: '1',
      patientName: 'John Doe',
      lastRecall: '09/18/2025',
      daysOverdue: 182,
      recallType: 'Prophy',
      provider: 'RDH Sarah Lee',
      status: 'Contacted',
    },
    {
      id: '2',
      patientName: 'Michael Brown',
      lastRecall: '08/20/2025',
      daysOverdue: 211,
      recallType: 'Perio maintenance',
      provider: 'RDH Sarah Lee',
      status: 'Not contacted',
    },
    {
      id: '3',
      patientName: 'Chris Lee',
      lastRecall: '07/14/2025',
      daysOverdue: 248,
      recallType: 'Prophy',
      provider: 'RDH Emily Davis',
      status: 'Not contacted',
    },
    {
      id: '4',
      patientName: 'Linda White',
      lastRecall: '06/22/2025',
      daysOverdue: 270,
      recallType: 'Perio maintenance',
      provider: 'RDH Emily Davis',
      status: 'Not contacted',
    },
    {
      id: '5',
      patientName: 'Alice Johnson',
      lastRecall: '11/02/2025',
      daysOverdue: 137,
      recallType: 'Prophy',
      provider: 'RDH Emily Davis',
      status: 'Scheduled',
    },
    {
      id: '6',
      patientName: 'Patricia Moore',
      lastRecall: '10/01/2025',
      daysOverdue: 169,
      recallType: 'Perio maintenance',
      provider: 'RDH Sarah Lee',
      status: 'Scheduled',
    },
  ],
}

export function getRecallOverduePatientRows(
  range: RangeKey,
): RecallOverduePatientRow[] {
  return OVERDUE_PATIENTS[range]
}
